import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { trackEvent, trackAdsConversion } from "@/lib/track";
import { SERVICES } from "@/data/services";

export default function AppointmentForm({ defaultService = "" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: defaultService,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Please enter your full name");
      return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 6) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!form.service) {
      toast.error("Please select a service");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        message: form.message.trim(),
      });
      // Fire conversion events only after successful backend persistence.
      trackEvent("appointment_submitted", {
        event_category: "lead",
        event_label: form.service,
        service: form.service,
      });
      trackEvent("generate_lead", {
        event_category: "lead",
        value: 1,
        currency: "INR",
      });
      // Google Ads conversion — add a conversion label later via track.js
      trackAdsConversion(null, { value: 1, currency: "INR" });
      setDone(true);
      toast.success("Thank you! We'll contact you shortly.");
      setForm({ name: "", phone: "", email: "", service: defaultService, message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-lg border border-primary/20 bg-primary/5 p-8 text-center"
        data-testid="appointment-success"
      >
        <h3 className="font-serif text-2xl text-primary mb-2">Request received</h3>
        <p className="text-foreground/75 text-sm">
          Our team will reach out to confirm your appointment shortly. For urgent
          concerns please call us directly.
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          className="mt-4 text-sm text-primary underline underline-offset-4"
          data-testid="appointment-send-another"
        >
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-5"
      data-testid="appointment-form"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="ap-name" className="text-xs uppercase tracking-wider text-muted-foreground">
            Full Name *
          </Label>
          <Input
            id="ap-name"
            required
            value={form.name}
            onChange={set("name")}
            className="mt-1.5"
            data-testid="appointment-name"
          />
        </div>
        <div>
          <Label htmlFor="ap-phone" className="text-xs uppercase tracking-wider text-muted-foreground">
            Phone Number *
          </Label>
          <Input
            id="ap-phone"
            required
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            className="mt-1.5"
            data-testid="appointment-phone"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="ap-email" className="text-xs uppercase tracking-wider text-muted-foreground">
          Email Address
        </Label>
        <Input
          id="ap-email"
          type="email"
          value={form.email}
          onChange={set("email")}
          className="mt-1.5"
          data-testid="appointment-email"
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          Service Required *
        </Label>
        <Select
          value={form.service}
          onValueChange={(v) => setForm({ ...form, service: v })}
        >
          <SelectTrigger className="mt-1.5 min-w-0 [&>span]:truncate" data-testid="appointment-service-trigger">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s.slug} value={s.name} data-testid={`appointment-service-${s.slug}`}>
                {s.name}
              </SelectItem>
            ))}
            <SelectItem value="Home Eye Check-up (Noida & Greater Noida)" data-testid="appointment-service-home-visit">Home Eye Check-up (Noida & Greater Noida)</SelectItem>
            <SelectItem value="Other" data-testid="appointment-service-other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="ap-message" className="text-xs uppercase tracking-wider text-muted-foreground">
          Message
        </Label>
        <Textarea
          id="ap-message"
          value={form.message}
          onChange={set("message")}
          rows={4}
          className="mt-1.5"
          data-testid="appointment-message"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
        data-testid="appointment-submit"
      >
        {loading ? "Sending…" : "Book Appointment"}
      </button>
      <p className="text-[11px] text-muted-foreground text-center">
        By submitting, you agree to be contacted by Nehchal Eye Centre regarding your enquiry.
      </p>
    </form>
  );
}
